import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'

import { Popper } from '@mui/material'
import cn from 'classnames'
import TextInput, { TextInputRef } from 'ext-ui/components/TextInput/TextInput'
import { base_colorAction } from 'ext-ui/style/colors'
import ChevronIcon from 'ext-ui/svg/icon/ChevronIcon'
import moment, { Moment } from 'moment-timezone'
import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import {
  DayPickerSingleDateController,
  isInclusivelyBeforeDay,
} from 'react-dates'
import SvgCalendar from 'src/components/Svgs/SvgCalendar'
import { useTranslate } from 'src/localization/Trns'

import style from './DateInput.style'

const CANDIDATE_PARSE_FORMATS = ['M/D/YYYY', 'MM/DD/YYYY', 'YYYY-M-D']

/** react-dates' own `monthFormat` default. */
const MONTH_CAPTION_FORMAT = 'MMMM YYYY'

/** What the legacy field rendered: a 24px chevron at `transform: scale(1.2)`. */
const NAV_CHEVRON_SIZE = 28.8

/**
 * How long to keep trying to focus a day after the calendar opens. Two things
 * delay it: MUI mounts the popper's portal from an effect, and react-dates
 * then keeps the month `visibility: hidden` for a frame or two while it
 * measures — and `focus()` on a hidden element is a silent no-op. Three frames
 * is what it takes in practice; the rest is headroom, and the cap only exists
 * so a calendar that never appears cannot leave this running.
 */
const MAX_FOCUS_FRAMES = 10

type NavButtonProps = {
  disabled: boolean
  onClick?: React.MouseEventHandler
}

/**
 * react-dates hands `navPrev`/`navNext` to a role="button" div that it leaves
 * without a tabIndex, so the arrows are unreachable by keyboard. The render
 * props give us the element itself, so they get to be real buttons.
 *
 * Only `onClick` is wired: react-dates also offers `onKeyUp` (which re-fires
 * the click for Enter/Space, as its div has to) and `onMouseUp` (which blurs
 * to hide the div's focus ring). A <button> gets both from the browser, and
 * taking them would navigate twice on Space.
 */
const renderNavButton = (
  direction: 'left' | 'right',
  testId: string,
  label: string,
  { disabled, onClick }: NavButtonProps,
): React.ReactElement => (
  <button
    type="button"
    className="calendar-nav"
    data-testid={testId}
    aria-label={label}
    // aria-disabled, not disabled: at a range boundary the button must keep
    // its place in the dialog's tab cycle, or the loop skips a stop and the
    // arrows become unreachable from the keyboard.
    aria-disabled={disabled || undefined}
    onClick={disabled ? undefined : onClick}
  >
    <ChevronIcon
      direction={direction}
      width={NAV_CHEVRON_SIZE}
      height={NAV_CHEVRON_SIZE}
      fill={base_colorAction}
      aria-hidden="true"
    />
  </button>
)

const POPPER_MODIFIERS = [
  {
    name: 'offset',
    // react-dates' DEFAULT_VERTICAL_SPACING: the gap SingleDatePicker leaves
    // between the field and the calendar, which the Popper has to reproduce.
    options: { offset: [0, 22] },
  },
]

export type YearRange = { min: number; max: number }

export type DateInputProps = {
  className?: string
  label?: string
  requirement?: 'optional' | 'required'
  value: Moment | undefined
  onChange: (value: Moment | undefined) => void
  errorText?: React.ReactNode
  microcopy?: string
  disabled?: boolean
  allowAllDates?: boolean
  allowPastAndCurrentDates?: boolean
  /** Already-translated; defaults to translate('Select a date'). */
  placeholder?: string
  displayFormat?: string
  /** Rendered beside the label. */
  tooltip?: React.ReactElement
  /**
   * Absolute years. Supplying it replaces the calendar's caption text with
   * month and year selects; a date of birth is otherwise hundreds of
   * previous-month clicks away.
   */
  yearRange?: YearRange
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'
  testId: string
}

const selectableYears = (range: YearRange, visibleYear: number): number[] => {
  const years: number[] = []
  for (let year = range.max; year >= range.min; year -= 1) years.push(year)
  // A `value` outside the range is still rendered rather than clamped, so the
  // select needs an option for it or it shows blank on a date the field itself
  // accepted.
  if (!years.includes(visibleYear)) {
    years.push(visibleYear)
    years.sort((a, b) => b - a)
  }
  return years
}

const DateInput = (props: DateInputProps): React.ReactElement => {
  const {
    className,
    label,
    requirement,
    value,
    onChange,
    errorText,
    microcopy,
    disabled,
    allowAllDates,
    allowPastAndCurrentDates,
    placeholder,
    displayFormat = 'll',
    tooltip,
    yearRange,
    placement = 'bottom-start',
    testId,
  } = props

  const translate = useTranslate()
  const popperId = useId()
  const hintId = `${popperId}-hint`
  const inputRef = useRef<TextInputRef>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const calendarRef = useRef<HTMLDivElement>(null)
  const calendarInteractionRef = useRef(false)
  const focusGridOnOpenRef = useRef(false)

  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const [focused, setFocused] = useState(false)
  const [draftRejected, setDraftRejected] = useState(false)

  const isOutsideRangeFn = useMemo(():
    | ((day: Moment) => boolean)
    | undefined => {
    if (allowAllDates) return (): boolean => false
    if (allowPastAndCurrentDates) {
      return (day: Moment): boolean => !isInclusivelyBeforeDay(day, moment())
    }
    return undefined
  }, [allowAllDates, allowPastAndCurrentDates])

  const parseFormats = useMemo(
    () => [displayFormat, ...CANDIDATE_PARSE_FORMATS],
    [displayFormat],
  )

  const closeCalendar = (): void => {
    setOpen(false)
  }

  // Commits only a real change: blur fires on every tab-through, and an
  // onChange there would dirty the consumer's form and clear its server
  // errors without the user having edited anything.
  const commitDraft = (): void => {
    if (draft.trim() === '') {
      setDraftRejected(false)
      if (value !== undefined) onChange(undefined)
      return
    }
    const parsed = moment(draft, parseFormats, true)
    const rejected =
      !parsed.isValid() || (isOutsideRangeFn ? isOutsideRangeFn(parsed) : false)
    if (rejected) {
      // Say so rather than reverting. Silently restoring the old value leaves
      // the user watching what they typed disappear with no account of why,
      // and nothing emitted either way.
      setDraftRejected(true)
      return
    }
    setDraftRejected(false)
    const committed = parsed.startOf('day')
    if (!value || !committed.isSame(value, 'day')) onChange(committed)
    setDraft(committed.format(displayFormat))
  }

  // Entering the field, whether or not the caret lands in the input. Focus
  // returning from a calendar interaction is not a fresh entry: the day the
  // user just picked is already in the draft, and `value` has not round-tripped
  // through the consumer yet, so re-seeding would show — and on the next blur
  // re-commit — the date they replaced.
  const beginEditing = (): void => {
    if (focused) return
    setFocused(true)
    setDraft(value ? value.format(displayFormat) : '')
  }

  const handleFocus = (): void => {
    if (focused) return
    beginEditing()
    setOpen(true)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
    const nextFocus = e.relatedTarget instanceof Node ? e.relatedTarget : null

    if (nextFocus && calendarRef.current?.contains(nextFocus)) {
      // Focus moved into the calendar with no mousedown to set the ref below —
      // an assistive-technology or programmatic move, and what Playwright's
      // selectOption does. Committing here would tear the popper down before
      // the change it is about to make can land.
      return
    }

    if (calendarInteractionRef.current) {
      // A mousedown on the calendar (day cell, nav button, caption select)
      // fires the input's blur first; let the resulting click/day-select
      // commit instead so a click on "today" can't be raced by a
      // revert-on-blur.
      calendarInteractionRef.current = false
      return
    }

    commitDraft()

    if (nextFocus && toggleRef.current?.contains(nextFocus)) {
      // Tabbing onto the calendar's own toggle is not leaving the field.
      // `focused` stays set so the button's focus() call cannot be read as a
      // fresh entry and re-open what it just closed.
      return
    }

    setFocused(false)
    closeCalendar()
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setDraftRejected(false)
    setDraft(e.target.value)
  }

  /**
   * Escape belongs to whichever layer is on top. While the calendar is open
   * that is this component, and the event must not travel any further: React
   * bubbles it through the tree to react-modal's own handler, which would take
   * the whole surrounding form down with the calendar. Closed, it is the
   * modal's to act on and we leave it alone.
   */
  const consumeEscape = (e: React.KeyboardEvent): boolean => {
    if (!open) return false
    e.preventDefault()
    e.stopPropagation()
    closeCalendar()
    setDraftRejected(false)
    setDraft(value ? value.format(displayFormat) : '')
    inputRef.current?.input?.focus()
    return true
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape') {
      consumeEscape(e)
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      commitDraft()
      closeCalendar()
    }
  }

  const handleDaySelect = (date: Moment | null): void => {
    if (!date) return
    setDraftRejected(false)
    const committed = date.startOf('day')
    onChange(committed)
    setDraft(committed.format(displayFormat))
    closeCalendar()
    inputRef.current?.input?.focus()
  }

  const handleCalendarFocusChange = ({
    focused: calendarFocused,
  }: {
    focused: boolean
  }): void => {
    if (!calendarFocused) closeCalendar()
  }

  // Nothing in the field's own chrome counts as outside. This runs on mouseup,
  // ahead of the toggle's click, so closing here would leave that click
  // reading a stale `open` and re-opening what the user meant to dismiss; and
  // clicking back into the input to fix a typo is not a request to put the
  // calendar away.
  const handleOutsideClick = (e: MouseEvent): void => {
    const target = e.target
    if (target instanceof Node) {
      if (toggleRef.current?.contains(target)) return
      if (inputRef.current?.container?.contains(target)) return
    }
    closeCalendar()
  }

  // `handleBlur` deliberately leaves the calendar open when focus lands on the
  // toggle, so tabbing onward from the button is the last way out of the field.
  // Without this the popper would stay up with focus somewhere else entirely —
  // nothing else closes it, since onOutsideClick only answers to clicks.
  const handleToggleBlur = (e: React.FocusEvent<HTMLButtonElement>): void => {
    const nextFocus = e.relatedTarget instanceof Node ? e.relatedTarget : null
    // Focus going back to the input is this component's own doing — every
    // close path re-focuses it — and clearing `focused` there would let
    // `handleFocus` read the arrival as a fresh entry and re-open.
    if (nextFocus && nextFocus === inputRef.current?.input) return
    if (nextFocus && calendarRef.current?.contains(nextFocus)) return
    setFocused(false)
    closeCalendar()
  }

  const calendarFocusables = (): HTMLElement[] =>
    Array.from(
      calendarRef.current?.querySelectorAll<HTMLElement>(
        'button, select, [tabindex]:not([tabindex="-1"])',
      ) ?? [],
    )

  /**
   * react-dates always marks one day `tabindex="0"` — the selected one, or the
   * first pickable day of the visible month. Focusing it directly rather than
   * through the `isFocused` prop is deliberate: that prop re-focuses the day
   * from a rAF on every subsequent render of the cell, which would drag focus
   * back out of the caption selects the moment the user tabbed to one.
   *
   * It cannot be done in the click handler that opens the calendar, though.
   * The grid is not in the document on that tick, and once it is it spends a
   * frame or two hidden while react-dates measures the month — so the element
   * can be found well before it will accept focus.
   */
  useEffect(() => {
    if (!open) {
      focusGridOnOpenRef.current = false
      return undefined
    }
    if (!focusGridOnOpenRef.current) return undefined

    let frames = 0
    let pending = 0
    const focusDay = (): void => {
      const day = calendarRef.current?.querySelector<HTMLElement>(
        '[role="button"][tabindex="0"]',
      )
      day?.focus()
      // Whether focus actually landed, not whether there was something to aim
      // at. The day exists a frame or two before it can take focus, and
      // `focus()` on a hidden element neither throws nor moves anything.
      if (day && document.activeElement === day) {
        focusGridOnOpenRef.current = false
        return
      }
      frames += 1
      if (frames > MAX_FOCUS_FRAMES) {
        focusGridOnOpenRef.current = false
        return
      }
      pending = requestAnimationFrame(focusDay)
    }
    focusDay()

    return (): void => {
      if (pending) cancelAnimationFrame(pending)
    }
  }, [open])

  const toggleCalendar = (e: React.MouseEvent<HTMLButtonElement>): void => {
    if (open) {
      closeCalendar()
      inputRef.current?.input?.focus()
      return
    }
    // `detail` is 0 only when the click came from Enter or Space. A pointer
    // user is on their way to click a day and wants the caret left alone; a
    // keyboard user has no other way in, because the popper portals out of the
    // modal that react-modal's tab scope is wrapping.
    // Opening from the toggle counts as entering the field even when the caret
    // never arrives: otherwise the Escape that closes the calendar re-focuses
    // the input, which reads as a fresh entry and re-opens it.
    beginEditing()
    focusGridOnOpenRef.current = e.detail === 0
    setOpen(true)
    if (!focusGridOnOpenRef.current) inputRef.current?.input?.focus()
  }

  const handleCalendarKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key === 'Escape') {
      // Capture, because react-dates stops this event bubbling out of the day
      // grid and routes Escape to a prop that does nothing by default.
      consumeEscape(e)
      return
    }
    if (e.key !== 'Tab') return
    // Same reason as Escape: react-modal scopes Tab to its own content, and
    // the popper is not in it. Left to bubble, its handler would pull focus
    // out of the calendar the user is moving around in.
    e.stopPropagation()
    // Nothing outside the popper is reachable by Tab anyway: it portals to
    // <body>, past everything react-modal scopes, so an unhandled Tab at the
    // last stop walks out of the page entirely.
    const focusables = calendarFocusables()
    if (focusables.length === 0) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const active = document.activeElement
    if (e.shiftKey && active === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && active === last) {
      e.preventDefault()
      first.focus()
    }
  }

  const renderMonthElement = yearRange
    ? ({
        month,
        onMonthSelect,
        onYearSelect,
        isVisible,
      }: {
        month: Moment
        onMonthSelect: (currentMonth: Moment, newMonthVal: string) => void
        onYearSelect: (currentMonth: Moment, newYearVal: string) => void
        isVisible: boolean
      }): React.ReactNode => {
        // The off-screen neighbouring months are rendered too; a second pair of
        // selects would duplicate both accessible names and test ids.
        if (!isVisible) {
          return <strong>{month.format(MONTH_CAPTION_FORMAT)}</strong>
        }
        return (
          <div className="caption-selects">
            <select
              className="caption-month"
              aria-label={translate('Month')}
              data-testid={`${testId}-month-select`}
              value={month.month()}
              onChange={(e): void => onMonthSelect(month, e.target.value)}
            >
              {moment.months().map((name, index) => (
                <option key={name} value={index}>
                  {name}
                </option>
              ))}
            </select>
            <select
              className="caption-year"
              aria-label={translate('Year')}
              data-testid={`${testId}-year-select`}
              value={month.year()}
              onChange={(e): void => onYearSelect(month, e.target.value)}
            >
              {selectableYears(yearRange, month.year()).map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        )
      }
    : undefined

  // react-dates ships English strings for the calendar's landmarks and for the
  // names it gives day cells. The prop replaces its defaults outright rather
  // than merging into them, so every phrase this configuration reads has to be
  // here — `chooseAvailableDate` included, even though returning the bare date
  // is exactly what the default does and moment has already localised it.
  const calendarPhrases = {
    calendarLabel: translate('Calendar'),
    roleDescription: translate('date picker'),
    jumpToPrevMonth: translate('Previous month'),
    jumpToNextMonth: translate('Next month'),
    chooseAvailableDate: ({ date }: { date: string }): string => date,
    dateIsSelected: ({ date }: { date: string }): string =>
      translate('Selected. ${date}', { date }),
    dateIsUnavailable: ({ date }: { date: string }): string =>
      translate('Not available. ${date}', { date }),
  }

  // A rejected draft outlives the blur that rejected it: falling back to the
  // committed value here would erase the very text the error is about.
  const displayValue =
    focused || draftRejected ? draft : value ? value.format(displayFormat) : ''

  return (
    <div className={cn('DateInputField', className)} css={style}>
      {/* The field takes typing as well as picking, which the calendar icon
          does not say and the placeholder has no room for. */}
      <span id={hintId} className="visually-hidden">
        {translate('Type a date as MM/DD/YYYY or choose one from the calendar')}
      </span>
      <TextInput
        ref={inputRef}
        label={label}
        requirement={requirement}
        tooltip={tooltip}
        aria-describedby={hintId}
        // A consumer's own validation outranks ours: theirs knows the field's
        // role in the form, ours only knows the text would not parse.
        errorText={
          errorText ??
          (draftRejected ? translate('Enter a valid date.') : undefined)
        }
        microcopy={microcopy}
        disabled={disabled}
        testId={testId}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder ?? translate('Select a date')}
        iconLeft={<SvgCalendar aria-hidden="true" />}
        trailingSlot={
          <button
            ref={toggleRef}
            type="button"
            className="calendar-toggle"
            data-testid={`${testId}-calendar-toggle`}
            aria-label={translate('Choose date')}
            aria-expanded={open}
            aria-haspopup="dialog"
            aria-controls={popperId}
            disabled={disabled}
            // Keeping focus in the input is what makes this a toggle: without
            // it the mousedown blurs the input, which closes the calendar
            // before the click can read `open` and decide.
            onMouseDown={(e): void => e.preventDefault()}
            onClick={toggleCalendar}
            onKeyDown={(e): void => {
              if (e.key === 'Escape') consumeEscape(e)
            }}
            onBlur={handleToggleBlur}
          >
            <ChevronIcon
              direction={open ? 'up' : 'down'}
              width={16}
              height={16}
              aria-hidden="true"
            />
          </button>
        }
      />
      <Popper
        open={open}
        anchorEl={inputRef.current?.container ?? null}
        placement={placement}
        modifiers={POPPER_MODIFIERS}
        css={style}
        className={cn('calendar-popper', {
          'has-caption-selects': yearRange !== undefined,
        })}
      >
        <div
          ref={calendarRef}
          id={popperId}
          role="dialog"
          // Not a focus trap: the field behind it stays usable, and Escape or
          // a click outside is always a way out.
          aria-modal="false"
          aria-label={translate('Choose date')}
          onKeyDownCapture={handleCalendarKeyDown}
          onMouseDownCapture={(): void => {
            // Only an interaction that is actually about to blur the input
            // needs deferring. react-dates parks focus on a day cell after
            // every caption change, so setting this unconditionally leaves a
            // flag no blur ever consumes — and the next real blur spends it
            // instead of committing what the user typed.
            if (document.activeElement === inputRef.current?.input) {
              calendarInteractionRef.current = true
            }
          }}
          onMouseUpCapture={(): void => {
            // The blur this defers fires during mousedown's default action, so
            // by mouseup the flag has either been consumed or was never going
            // to be. Either way it must not outlive the gesture that set it.
            calendarInteractionRef.current = false
          }}
        >
          <DayPickerSingleDateController
            date={value ?? null}
            onDateChange={handleDaySelect}
            focused={open}
            onFocusChange={handleCalendarFocusChange}
            onOutsideClick={handleOutsideClick}
            isOutsideRange={isOutsideRangeFn}
            initialVisibleMonth={(): Moment => value ?? moment()}
            renderMonthElement={renderMonthElement}
            renderNavPrevButton={(navProps): React.ReactElement =>
              renderNavButton(
                'left',
                `${testId}-prev-month`,
                translate('Previous month'),
                navProps,
              )
            }
            renderNavNextButton={(navProps): React.ReactElement =>
              renderNavButton(
                'right',
                `${testId}-next-month`,
                translate('Next month'),
                navProps,
              )
            }
            phrases={calendarPhrases}
            numberOfMonths={1}
            hideKeyboardShortcutsPanel
          />
        </div>
      </Popper>
    </div>
  )
}

export default DateInput
